import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/core/styles'

import { MaxConversationGroup, MaxConversationSummary } from '../../types'
import ConversationsList from '../sidebar/conversations-list'
import SidebarHeader from '../sidebar/sidebar-header'
import SidebarToolbar from '../sidebar/sidebar-toolbar'
import UserProfileCard from '../ui/user-profile-card'
import { SIDEBAR_EXPANDED_WIDTH_PX } from './sidebar-layout'

type SidebarPanelProps = {
  searchQuery: string
  isLoadingConversations: boolean
  groupedConversationSummaries: MaxConversationGroup[]
  activeConversationId: string | null
  userName?: string | null
  userEmail?: string | null
  tenantName?: string | null
  onSearchChange: (value: string) => void
  onNewChat: () => void
  onSelectConversation: (conversationId: string) => void
  onRenameConversation: (conversation: MaxConversationSummary, nextTitle: string) => Promise<void> | void
  onTogglePinConversation: (conversation: MaxConversationSummary) => Promise<void> | void
  onDeleteConversation: (conversation: MaxConversationSummary) => Promise<void> | void
  onCollapseSidebar: () => void
  onSignOut?: () => void
}

const useStyles = makeStyles(() => ({
  root: {
    width: SIDEBAR_EXPANDED_WIDTH_PX,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'var(--ds-bg-surface, #fff)',
    backdropFilter: 'blur(20px) saturate(1.8)',
    WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
    borderRight: '1px solid var(--ds-border-default, #d7dae4)',
    transition: 'all 200ms ease',
  },
  headerShell: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    padding: '32px 20px',
    marginLeft: -4,
  },
  contentShell: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  toolbarShell: {
    padding: '0 14px 14px',
    flexShrink: 0,
  },
  listLoading: {
    padding: '8px 0',
    display: 'flex',
    justifyContent: 'center',
  },
  bottomSection: {
    flexShrink: 0,
    borderTop: '1px solid var(--ds-border-default, #d7dae4)',
  },
  helpLink: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px',
    fontSize: '0.875rem',
    color: 'var(--ds-fg-secondary, #535b6c)',
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    textDecoration: 'none',
    transition: 'color 150ms ease, background-color 150ms ease',
    '&:hover': {
      color: 'var(--ds-fg-primary, #1f2430)',
      backgroundColor: 'var(--ds-bg-subtle-hover, #e7eaf1)',
    },
  },
}))

const SidebarPanel = ({
  searchQuery,
  isLoadingConversations,
  groupedConversationSummaries,
  activeConversationId,
  userName,
  userEmail,
  tenantName,
  onSearchChange,
  onNewChat,
  onSelectConversation,
  onRenameConversation,
  onTogglePinConversation,
  onDeleteConversation,
  onCollapseSidebar,
  onSignOut,
}: SidebarPanelProps) => {
  const classes = useStyles()

  return (
    <aside className={`max-sidebar-panel group ${classes.root}`}>
      <div className={classes.headerShell}>
        <SidebarHeader onCollapseSidebar={onCollapseSidebar} />
      </div>

      <div className={classes.contentShell}>
        <div className={classes.toolbarShell}>
          <SidebarToolbar
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            onNewChat={onNewChat}
          />
        </div>

        {isLoadingConversations && groupedConversationSummaries.length === 0 ? (
          <div className={classes.listLoading}>
            <CircularProgress size={18} />
          </div>
        ) : (
          <ConversationsList
            groups={groupedConversationSummaries}
            activeId={activeConversationId}
            onSelectConversation={onSelectConversation}
            onRenameConversation={onRenameConversation}
            onTogglePinConversation={onTogglePinConversation}
            onDeleteConversation={onDeleteConversation}
          />
        )}
      </div>

      <div className={classes.bottomSection}>
        <a
          href="https://pagmaxchat.app/help-center"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.helpLink}
        >
          <svg
            width={19}
            height={19}
            style={{ flexShrink: 0 }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>Help Center</span>
        </a>

        {(userEmail || userName) && (
          <UserProfileCard
            userName={userName}
            userEmail={userEmail}
            tenantName={tenantName}
            onSignOut={onSignOut}
          />
        )}
      </div>
    </aside>
  )
}

export default SidebarPanel
